import { renderMarkdownToSafeHtml } from './markdownRenderer';
import { getIconOptionLabel } from './iconOptionLabels';

const normalizeWhitespace = value => value?.replace(/\s+/g, ' ').trim() || '';
export const TRANSCRIPT_NAVIGATION_INSTRUCTIONS =
  'Use up and down arrow keys to navigate between messages. Press Enter to interact with message contents. Press Escape to exit message contents.';
const MAX_OPTION_ANNOUNCEMENTS = 3;
const BOT_SPEAKER_PREFIX = 'VA chatbot said.';

const extractTextFromHtml = html => {
  if (!html) {
    return '';
  }

  const container = document.createElement('div');
  container.innerHTML = html;
  return normalizeWhitespace(container.textContent);
};

/**
 * Converts markdown-like chatbot text into speakable plain text.
 *
 * @param {string} text
 * @returns {string}
 */
export const getSpeakableMessageText = text => {
  const safeHtml = renderMarkdownToSafeHtml(text);
  return extractTextFromHtml(safeHtml);
};

const getSpeakableOptionText = option => {
  const normalizedOption = normalizeWhitespace(option);
  if (!normalizedOption) {
    return '';
  }

  return getIconOptionLabel(normalizedOption);
};

/**
 * @typedef {Object} ChatMessageLike
 * @property {string} id
 * @property {'user'|'bot'} sender
 * @property {string} text
 * @property {string[]} [options]
 */

/**
 * Builds announcement text for a bot message, including response options.
 *
 * @param {ChatMessageLike} message
 * @param {Object} [options]
 * @param {string} [options.disclaimerText] - AI disclaimer to append so screen
 *   reader users hear it as part of the response (RAG answers only).
 * @param {boolean} [options.includeSpeakerPrefix] - Set false when the text is
 *   appended to an announcement that already opened with the speaker prefix
 *   (e.g. a feedback prompt folded into its answer).
 * @returns {string}
 */
export const buildBotMessageAnnouncementText = (
  message,
  { disclaimerText = '', includeSpeakerPrefix = true } = {},
) => {
  if (!message || message.sender !== 'bot') {
    return '';
  }

  const text = getSpeakableMessageText(message.text);
  const options = Array.isArray(message.options)
    ? message.options
        .map(option => getSpeakableOptionText(option))
        .filter(Boolean)
    : [];

  const disclaimerSuffix =
    disclaimerText && message.isAiGenerated
      ? ` ${normalizeWhitespace(disclaimerText)}`
      : '';

  const speakerPrefix = includeSpeakerPrefix ? `${BOT_SPEAKER_PREFIX} ` : '';

  if (options.length === 0) {
    return normalizeWhitespace(`${speakerPrefix}${text}${disclaimerSuffix}`);
  }

  const summarizedOptions = options.slice(0, MAX_OPTION_ANNOUNCEMENTS);
  const omittedCount = options.length - summarizedOptions.length;
  const suffix = omittedCount > 0 ? `, and ${omittedCount} more` : '';

  return normalizeWhitespace(
    `${speakerPrefix}${text}${disclaimerSuffix} Options: ${summarizedOptions.join(
      ', ',
    )}${suffix}.`,
  );
};
